import { useContext, useState, useEffect, useRef } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';
import { env } from '@lcsc-ui/utils/baseUrl';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import { Spin } from 'antd';

import { StyledTable2 } from '../index.style';

import FeedbackModal from './FeedbackModal';

import { getSelectionProductPage } from '@/apis/selection';
import LcPagination from '@/components/LcPagination';

const MainContent = ({ catalogList, selectedKeys, selectionProductPageData }) => {
  const { resourceData } = useContext(LayoutContext);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [tableData, setTableData] = useState(selectionProductPageData?.dataList || []);
  const [page, setPage] = useState({
    current: selectionProductPageData?.currPage || 1,
    pageSize: selectionProductPageData?.pageRow || 10,
    total: selectionProductPageData?.totalRow || 0,
  });

  const prevSelectedKeysRef = useRef();

  useEffect(() => {
    const prevSelected = prevSelectedKeysRef.current?.[0];
    const currSelected = selectedKeys?.[0];
    const isSelectedChanged = prevSelected !== currSelected;

    prevSelectedKeysRef.current = selectedKeys;

    if (isSelectedChanged) {
      // 如果目录变化了，且当前页不是第一页，先跳到第一页（触发 fetch）
      if (page.current !== 1) {
        setPage((prev) => ({ ...prev, current: 1 }));
        return;
      }
    }

    // 否则直接请求
    fetchData();
  }, [selectedKeys, page.current]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const pageCb = (current) => {
    setPage((prev) => {
      return {
        ...prev,
        current,
      };
    });
  };

  const fetchData = async () => {
    setLoading(true);

    // 提取目录层级参数
    let selectionModule = '';
    let selectionFunction = '';
    let selectionSegment = '';

    if (selectedKeys && selectedKeys.length > 0 && selectedKeys[0] !== '0') {
      const parts = selectedKeys[0].split('-');
      let currentNode = catalogList;

      parts.forEach((part, index) => {
        const idx = parseInt(part);
        if (currentNode[idx]) {
          const title = currentNode[idx].originalName || currentNode[idx].title;
          switch (index) {
            case 0:
              selectionModule = title;
              break;
            case 1:
              selectionFunction = title;
              break;
            case 2:
              selectionSegment = title;
              break;
          }
          currentNode = currentNode[idx].children || [];
        }
      });
    }

    const params = {
      currentPage: page.current,
      pageSize: page.pageSize,
    };

    const isHot = selectedKeys?.[0] === '0';

    if (isHot) {
      params.isHot = true; // 热门推荐
    } else {
      params.selectionModule = selectionModule; // 一级目录
      params.selectionFunction = selectionFunction; // 二级目录
      params.selectionSegment = selectionSegment; // 三级目录
    }

    const res = await getSelectionProductPage(params);

    setTableData(res?.result?.dataList || []);
    setPage((prev) => {
      return {
        ...prev,
        total: res?.result?.totalRow || 0,
      };
    });
    setLoading(false);
  };

  // 根据选中的key获取路径标题
  const getTitlePath = (hasSuffix = true) => {
    if (!selectedKeys || selectedKeys.length === 0) {
      return '请选择功能模块';
    }

    const key = selectedKeys[0];
    const parts = key.split('-');
    let currentNode = catalogList;
    const path = [];

    if (key === '0') {
      return currentNode[0].title;
    }

    for (const part of parts) {
      const index = parseInt(part);
      if (currentNode[index]) {
        path.push(currentNode[index].title);
        if (currentNode[index].children) {
          currentNode = currentNode[index].children;
        }
      }
    }

    return path.join('-') + (hasSuffix ? '方案推荐' : '');
  };

  const renderApplicationTag = (_, { applicationTag }) => {
    const tagList = applicationTag?.split('、') || [];
    if (!tagList.length) {
      return '-';
    }
    // 最多显示5行
    if (tagList.length > 5) {
      return (
        <>
          {tagList.slice(0, 5).map((tag, index) => (
            <div title={tag} className="max-w-[160px] truncate" key={index}>
              {tag}
            </div>
          ))}
          <div className="max-w-[160px] truncate cursor-pointer" title={tagList.join('\n')}>
            ......
          </div>
        </>
      );
    } else {
      return tagList.map((tag, index) => (
        <div title={tag} className="max-w-[160px] truncate" key={index}>
          {tag}
        </div>
      ));
    }
  };

  const columns = [
    {
      title: '主控方案型号',
      dataIndex: 'productModel',
      key: 'productModel',
    },
    {
      title: '品牌',
      dataIndex: 'brandName',
      key: 'brandName',
      width: 160,
    },
    {
      title: '封装',
      dataIndex: 'encapStandard',
      key: 'encapStandard',
      width: 120,
    },
    {
      title: '芯片特点',
      dataIndex: 'applicationTag',
      key: 'applicationTag',
      render: renderApplicationTag,
    },
    {
      title: '参考样品价',
      dataIndex: 'samplePrice',
      key: 'samplePrice',
      width: 120,
      render: (_, { samplePrice, isShowPrice }) => {
        return isShowPrice === false ? '需询价' : samplePrice ? `￥${samplePrice}` : '-';
      },
    },
    {
      title: '最低批量价',
      dataIndex: 'batchPrice',
      key: 'batchPrice',
      width: 120,
      render: (_, { batchPrice, isShowPrice }) => {
        return isShowPrice === false ? '需询价' : batchPrice ? `￥${batchPrice}` : '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, { uuid }) => (
        <div className="flex">
          <a
            data-spm="it"
            href={`${env === 'fat' ? 'https://fat-www-next.szlcsc.com' : resourceData?.projectConfigVO?.lcscWwwUrl}/selection/${uuid}.html`}
            target="_blank"
            onClick={() => handleBuriedPoint(uuid)}
          >
            <button className="text-lcBlue">查看详情</button>
          </a>
        </div>
      ),
    },
  ];

  const handleBuriedPoint = (uuid) => {
    buriedPoint({
      ty: 'click',
      cp: 'selection_list',
      op: 'go_detail',
      pf: 'pc',
      data: {
        catalog: getTitlePath(false),
        uuid,
      },
    });
  };

  return (
    <div className="flex-1 ml-[20px]">
      <Spin indicator={<LoadingOutlined spin />} spinning={loading}>
        <div data-spm="li" className="bg-white rounded-[20px] overflow-hidden">
          <div className="py-[26px] px-[20px] flex items-center justify-between">
            <h1 className="text-[24px] font-bold">{getTitlePath()}</h1>
            <button className="text-[#FF7800] text-[14px]" onClick={() => setIsOpen(true)}>
              欢迎填写选型反馈
            </button>
          </div>
          <StyledTable2
            columns={columns}
            dataSource={tableData}
            pagination={false}
            locale={{ emptyText: '暂无数据' }}
          />
        </div>
        <LcPagination
          className="mr-0"
          showSizeChanger={false}
          size="small"
          currentPage={page?.current}
          pageSize={page?.pageSize}
          total={page?.total}
          pageCb={pageCb}
        />
      </Spin>
      <FeedbackModal open={isOpen} onClose={handleClose} />
    </div>
  );
};

export default MainContent;
